import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { Product } from '../App';
export function ProductSection({
  product,
  index
}: {
  product: Product;
  index: number;
}) {
  const reversed = index % 2 !== 0;
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  // Subtle parallax for the product image
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
  return <section ref={sectionRef} className="relative w-full min-h-[200vh] bg-white" data-id="element-202">
      {/* Divider between products */}
      {index > 0 && <div className="absolute top-0 left-0 w-full flex items-center justify-center z-20" data-id="element-203">
          <div className="w-full h-px bg-gradient-to-r from-petrol/5 via-hoefelmeyer-green/50 to-petrol/5" data-id="element-204"></div>
          <motion.div initial={{
        scale: 0
      }} whileInView={{
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} className="absolute bg-white border border-hoefelmeyer-green/30 text-hoefelmeyer-green text-xs font-bold px-4 py-1.5 rounded-full shadow-sm" data-id="element-205">
            {String(index + 1).padStart(2, '0')}
          </motion.div>
        </div>}

      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-full flex flex-col lg:flex-row relative" data-id="element-206">
        {/* Sticky Image Column */}
        <div className={`lg:w-1/2 sticky top-0 h-screen flex items-center justify-center overflow-hidden ${reversed ? 'lg:order-2' : 'lg:order-1'}`} data-id="element-207">
          <ProductSpotlight imageUrl={product.imageUrl} y={y} data-id="element-208" />
        </div>

        {/* Scrolling Content Column */}
        <div className={`lg:w-1/2 py-[40vh] lg:py-[50vh] flex flex-col justify-center ${reversed ? 'lg:order-1' : 'lg:order-2'} ${reversed ? 'lg:pr-12 xl:pr-24' : 'lg:pl-12 xl:pl-24'}`} data-id="element-209">
          <ProductContent product={product} index={index} data-id="element-210" />
        </div>
      </div>
    </section>;
}
function ProductSpotlight({
  imageUrl,
  y
}: {
  imageUrl: string;
  y: any;
}) {
  return <div className="relative w-full max-w-[600px] aspect-square flex items-center justify-center" data-id="element-211">
      {/* Radial Glow Background */}
      <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,rgba(31,73,84,0.06)_0%,transparent_70%)]" data-id="element-212"></div>

      {/* Thin Green Ring */}
      <div className="absolute inset-[15%] rounded-full border border-hoefelmeyer-green/20" data-id="element-213"></div>

      {/* Grid Pattern inside spotlight */}
      <div className="absolute inset-[15%] rounded-full overflow-hidden opacity-20" style={{
      backgroundImage: 'radial-gradient(rgba(31,73,84,0.4) 1px, transparent 1px)',
      backgroundSize: '24px 24px',
      backgroundPosition: 'center center'
    }} data-id="element-214"></div>

      {/* Grounding Shadow */}
      <div className="absolute bottom-[15%] left-1/2 -translate-x-1/2 w-2/3 h-8 bg-petrol/20 blur-xl rounded-[100%]" data-id="element-215"></div>

      {/* Product Image with floating and parallax */}
      <motion.div style={{
      y
    }} className="relative z-10 w-[85%] h-[85%] flex items-center justify-center" data-id="element-216">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-20%'
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="w-full h-full flex items-center justify-center" data-id="element-217">
          <motion.img src={imageUrl} alt="Product" className="max-w-full max-h-full object-contain drop-shadow-2xl" animate={{
          y: [-12, 12, -12]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-218" />
        </motion.div>
      </motion.div>
    </div>;
}
function ProductContent({
  product,
  index
}: {
  product: Product;
  index: number;
}) {
  return <div className="w-full" data-id="element-219">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-20%'
    }} transition={{
      duration: 0.6
    }} data-id="element-220">
        <div className="flex items-center gap-3 mb-6" data-id="element-221">
          <span className="w-2 h-2 bg-hoefelmeyer-green rounded-full" data-id="element-222"></span>
          <span className="text-xs font-bold tracking-widest text-hoefelmeyer-green uppercase" data-id="element-223">
            Produkt {String(index + 1).padStart(2, '0')}
          </span>
        </div>

        <h3 className="text-4xl md:text-5xl font-extrabold text-petrol mb-6 tracking-tight leading-tight" data-id="element-224">
          {product.name}
        </h3>

        <p className="text-lg text-slate-600 mb-12 leading-relaxed font-light" data-id="element-225">
          {product.description}
        </p>
      </motion.div>

      {/* Vorteile Grid */}
      <div className="mb-12" data-id="element-226">
        <h4 className="text-sm font-bold tracking-widest text-slate-400 uppercase mb-6" data-id="element-227">
          Vorteile auf einen Blick
        </h4>
        <div className="grid grid-cols-2 gap-x-6 gap-y-8" data-id="element-228">
          {product.vorteile.map((v, i) => {
          const Icon = (LucideIcons as any)[v.icon] || LucideIcons.CheckCircle;
          return <motion.div key={i} className="flex items-start gap-4" initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-10%'
          }} transition={{
            duration: 0.4,
            delay: i * 0.1,
            type: 'spring'
          }} data-id="element-229">
                <div className="w-10 h-10 rounded bg-hoefelmeyer-green/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-230">
                  <Icon className="text-hoefelmeyer-green" size={20} strokeWidth={2} data-id="element-231" />
                </div>
                <span className="text-sm font-medium text-slate-700 leading-snug" data-id="element-232">
                  {v.text}
                </span>
              </motion.div>;
        })}
        </div>
      </div>

      {/* USPs List */}
      <div className="mb-12" data-id="element-233">
        <h4 className="text-sm font-bold tracking-widest text-slate-400 uppercase mb-6" data-id="element-234">
          Key Features
        </h4>
        <ul className="space-y-4" data-id="element-235">
          {product.usps.map((usp, i) => <motion.li key={i} className="flex items-center gap-3" initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-10%'
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} data-id="element-236">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-hoefelmeyer-green/20 flex items-center justify-center" data-id="element-237">
                <LucideIcons.Check className="text-hoefelmeyer-green" size={12} strokeWidth={3} data-id="element-238" />
              </div>
              <span className="text-slate-700 font-medium" data-id="element-239">{usp}</span>
            </motion.li>)}
        </ul>
      </div>

      {/* Ideal Für Chips */}
      <div data-id="element-240">
        <h4 className="text-sm font-bold tracking-widest text-slate-400 uppercase mb-4" data-id="element-241">
          Ideal für
        </h4>
        <div className="flex flex-wrap gap-2" data-id="element-242">
          {product.idealFor.map((item, i) => <motion.div key={i} className="px-4 py-2 rounded-full border border-slate-200 text-xs font-bold text-petrol bg-slate-50" initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-10%'
        }} transition={{
          duration: 0.3,
          delay: i * 0.05
        }} data-id="element-243">
              {item}
            </motion.div>)}
        </div>
      </div>
    </div>;
}